import { useId, useState } from "react";

import { Button, Card, Input, Label, StatusMessage } from "@ceedero/ui";

import { RecoveryForm } from "@/account/components/recovery-form";
import { useSignIn } from "@/account/hooks/use-account";
import { BrandMark } from "@/components/brand-mark";
import { resolveSignInErrorKey } from "@/account/utils/account-refusal";
import { useTranslation } from "@/localization/hooks/use-translation";

import type { TranslationKey } from "@/localization/hooks/use-translation";

interface FieldErrors {
  password?: TranslationKey;
  username?: TranslationKey;
}

/**
 * The signed-out surface, and the only one there is.
 *
 * Everything this application shows belongs to somebody, so there is nothing to
 * display before a sign-in — but it still says what an account is *for* rather
 * than only demanding one, because a visitor arriving from the Landing page may
 * be deciding whether they want one at all.
 *
 * Validation is the application's own: a native `required` popup is unstyleable
 * and appears in the operating system's language over a page drawn in the
 * user's chosen one.
 */
export const SignInPage = () => {
  const { t } = useTranslation();
  const signIn = useSignIn();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [isRecovering, setIsRecovering] = useState(false);
  const usernameId = useId();
  const passwordId = useId();
  const usernameErrorId = useId();
  const passwordErrorId = useId();

  const refusalKey = resolveSignInErrorKey(signIn.error);

  const submit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const errors: FieldErrors = {};
    if (username.trim().length === 0) {
      errors.username = "account.signIn.error.usernameRequired";
    }
    if (password.length === 0) {
      errors.password = "account.signIn.error.passwordRequired";
    }
    setFieldErrors(errors);
    if (errors.username !== undefined || errors.password !== undefined) {
      return;
    }
    signIn.mutate({ password, username });
  };

  /** Cleared on touch rather than re-checked per keystroke, which only nags. */
  const clearFieldError = (field: keyof FieldErrors) => {
    setFieldErrors((errors) =>
      errors[field] === undefined ? errors : { ...errors, [field]: undefined },
    );
  };

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-6 px-4 py-10">
      <div className="flex flex-col items-center gap-2 text-center">
        {/*
          Mark and logotype are one object, so they sit on one line and are
          sized against each other rather than against the text below — the same
          lockup the Landing page header uses, because two arrangements of one
          logo is two logos.

          `pt-0.5` rather than `items-center` alone: Fewt's cap height sits high
          in its em box, and optically centring it against the mark takes a
          nudge the box model will not give. Nothing sets a weight — one face
          ships, and a browser asked for a bold it has not been given fakes one
          by smearing the outlines.
        */}
        <h1 className="flex items-center gap-2.5">
          <BrandMark className="size-11 shrink-0 text-primary" />
          <span className="font-wordmark pt-0.5 text-[2.25rem] leading-none tracking-[0.02em]">
            {t("app.name")}
          </span>
        </h1>
        <p className="text-sm text-muted-foreground">{t("app.tagline")}</p>
      </div>

      <Card className="p-6">
        {isRecovering ? (
          <RecoveryForm
            onDone={(recoveredUsername) => {
              // Back to the form the user came from, with the name they were
              // just told already in it and the refusal that sent them away
              // gone.
              if (recoveredUsername !== undefined) {
                setUsername(recoveredUsername);
              }
              setPassword("");
              signIn.reset();
              setIsRecovering(false);
            }}
          />
        ) : (
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <h2 className="text-lg font-semibold">{t("account.signIn.title")}</h2>
              <p className="text-sm text-muted-foreground">{t("account.signIn.description")}</p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor={usernameId}>{t("account.signIn.username")}</Label>
              <Input
                id={usernameId}
                value={username}
                onChange={(event) => {
                  setUsername(event.target.value);
                  clearFieldError("username");
                }}
                placeholder={t("account.signIn.usernamePlaceholder")}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                aria-invalid={fieldErrors.username === undefined ? undefined : true}
                aria-describedby={fieldErrors.username === undefined ? undefined : usernameErrorId}
              />
              {fieldErrors.username !== undefined && (
                <StatusMessage id={usernameErrorId} tone="error">
                  {t(fieldErrors.username)}
                </StatusMessage>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor={passwordId}>{t("account.signIn.password")}</Label>
              <Input
                id={passwordId}
                type="password"
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  clearFieldError("password");
                }}
                autoComplete="current-password"
                aria-invalid={fieldErrors.password === undefined ? undefined : true}
                aria-describedby={fieldErrors.password === undefined ? undefined : passwordErrorId}
              />
              {fieldErrors.password !== undefined && (
                <StatusMessage id={passwordErrorId} tone="error">
                  {t(fieldErrors.password)}
                </StatusMessage>
              )}
            </div>

            {refusalKey !== undefined && (
              <StatusMessage tone="error">{t(refusalKey)}</StatusMessage>
            )}

            <Button type="submit" isLoading={signIn.isPending}>
              {t("account.signIn.submit")}
            </Button>
            {/* Beside the refusal it answers. A sign-in page with no way back in
              is where a forgotten password used to end. */}
            <Button type="button" variant="link" onClick={() => setIsRecovering(true)}>
              {t("account.signIn.forgotPassword")}
            </Button>
          </form>
        )}
      </Card>
    </main>
  );
};
